import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, PageHeader, Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Link as LinkIcon, Unlink, CheckCircle2, Mail, HardDrive } from "lucide-react";
import EmailTemplatesSection from "@/components/EmailTemplatesSection";

export default function SettingsPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);
  const [google, setGoogle] = useState(null);

  const loadGoogle = async () => {
    const { data } = await api.get("/integrations/google/status");
    setGoogle(data);
  };

  useEffect(() => {
    loadGoogle();
    const flag = params.get("google");
    if (flag === "ok") toast.success("Google conectado correctamente");
    if (flag === "error") toast.error(`Error conectando Google: ${params.get("msg") || ""}`);
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== next2) { toast.error("Las contraseñas no coinciden"); return; }
    if (next.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Contraseña actualizada");
      setCur(""); setNext(""); setNext2("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const connectGoogle = async () => {
    const { data } = await api.get("/integrations/google/connect");
    window.location.href = data.auth_url;
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("¿Desconectar Google? Dejarán de funcionar los envíos por correo y la subida a Drive.")) return;
    await api.post("/integrations/google/disconnect");
    toast.success("Google desconectado");
    loadGoogle();
  };

  const [syncing, setSyncing] = useState(false);
  const syncDriveFolders = async () => {
    const dom = window.prompt("Compartir CRM con dominio (ej. allactive.cl). Dejar vacío para no compartir:", "allactive.cl");
    if (dom === null) return;
    setSyncing(true);
    try {
      const fd = new FormData();
      if (dom) fd.append("share_domain", dom);
      const { data } = await api.post("/integrations/drive/sync-folders", fd);
      const r = data.root || {};
      const s = data.sync || {};
      toast.success(`Drive sincronizado · ${s.customers_created} carpetas nuevas · ${s.subfolders_created} subcarpetas${r.shared_with_domain ? ` · compartido con ${dom}` : ""}`);
      if (r.share_error) {
        toast.error(`No se pudo compartir con dominio: ${r.share_error.slice(0, 120)}`);
      }
      if (r.webViewLink) window.open(r.webViewLink, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const [running, setRunning] = useState("");
  const [uf, setUf] = useState(null);

  useEffect(() => { api.get("/uf-rate").then(({ data }) => setUf(data.uf)); }, []);

  const runJob = async (label, url) => {
    if (!window.confirm(`¿Ejecutar ${label} ahora?`)) return;
    setRunning(url);
    try {
      const { data } = await api.post(url);
      toast.success(`OK: ${JSON.stringify(data)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRunning("");
    }
  };

  return (
    <PageContainer>
      <PageHeader kicker="Configuración" title="Ajustes" subtitle="Gestión de cuenta, seguridad e integraciones." />

      {/* Automations */}
      <section className="mb-8 bg-white border border-neutral-200 p-6" data-testid="settings-automations-section">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Automatizaciones</div>
            <div className="font-display font-bold text-xl mt-1">Tareas programadas</div>
            <div className="text-sm text-neutral-500 mt-1">
              Diariamente 08:00 se envían recordatorios 24h antes a contactos marcados. El día 1 de cada mes a las 06:00 se generan los cobros mensuales en CLP usando el valor UF de hoy.
            </div>
          </div>
          {uf != null && (
            <div className="text-right shrink-0">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">UF hoy</div>
              <div className="font-mono-kpi font-bold text-lg text-[#FF5A00]">${Math.round(uf).toLocaleString("es-CL")}</div>
            </div>
          )}
        </div>
        {user?.role === "admin" && (
          <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap items-center gap-2">
            <button
              data-testid="run-visit-reminders"
              disabled={running !== ""}
              onClick={() => runJob("recordatorios de visita 24h", "/cron/notify-visits")}
              className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] bg-white border border-neutral-300 hover:border-[#FF5A00] text-[#0A0A0A] px-3 py-2 disabled:opacity-50"
            >
              Enviar recordatorios de mañana
            </button>
            <button
              data-testid="run-monthly-charges"
              disabled={running !== ""}
              onClick={() => runJob("cobros del mes", "/cron/monthly-charges")}
              className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] bg-white border border-neutral-300 hover:border-[#FF5A00] text-[#0A0A0A] px-3 py-2 disabled:opacity-50"
            >
              Generar cobros del mes
            </button>
            <span className="text-xs text-neutral-400">Sólo admins pueden ejecutar manualmente.</span>
          </div>
        )}
      </section>

      {/* Google integration */}
      <section className="mb-8 bg-white border border-neutral-200 p-6" data-testid="settings-google-section">        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Integración Google</div>
            <div className="font-display font-bold text-xl mt-1">Gmail + Google Drive</div>
            <div className="text-sm text-neutral-500 mt-1 max-w-xl">
              Conecta la cuenta corporativa de All Active para enviar cotizaciones por correo y guardar fotos/PDFs en Drive automáticamente.
            </div>
          </div>
          {google?.connected ? (
            <div className="text-right">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#00C853] inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Conectado
              </div>
              <div className="text-xs text-neutral-600 mt-1">{google.email}</div>
            </div>
          ) : (
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">No conectado</div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
          <div className="border border-neutral-200 p-3">
            <Mail className="w-4 h-4 text-[#FF5A00] mb-2" />
            <div className="font-bold text-sm">Envío automático</div>
            <div className="text-xs text-neutral-500 mt-0.5">Cotizaciones, recordatorios y alertas</div>
          </div>
          <div className="border border-neutral-200 p-3">
            <HardDrive className="w-4 h-4 text-[#FF5A00] mb-2" />
            <div className="font-bold text-sm">Google Drive</div>
            <div className="text-xs text-neutral-500 mt-0.5">Carpeta por cliente con fotos/PDFs</div>
          </div>
          <div className="border border-neutral-200 p-3">
            <ShieldCheck className="w-4 h-4 text-[#00C853] mb-2" />
            <div className="font-bold text-sm">OAuth seguro</div>
            <div className="text-xs text-neutral-500 mt-0.5">Solo permiso de envío y subida</div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-neutral-100">
          {google?.connected ? (
            <button onClick={disconnectGoogle} data-testid="google-disconnect" className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 border border-[#D50000]/30 px-3 py-2">
              <Unlink className="w-3 h-3 inline mr-1 -mt-0.5" /> Desconectar Google
            </button>
          ) : (
            <PrimaryButton data-testid="google-connect" onClick={connectGoogle}>
              <LinkIcon className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Conectar Google
            </PrimaryButton>
          )}
          <span className="text-xs text-neutral-400">
            {user?.role === "admin" ? "Solo administradores pueden desconectar." : "Los operadores podrán usar la conexión ya establecida por un admin."}
          </span>
        </div>
      </section>

      <EmailTemplatesSection isAdmin={user?.role === "admin"} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-[#FF5A00]" />
            <div className="font-display font-bold text-lg">Tu cuenta</div>
          </div>
          <div className="space-y-2 text-sm">
            <div><span className="text-neutral-500">Nombre:</span> <span className="font-medium">{user?.name}</span></div>
            <div><span className="text-neutral-500">Email:</span> <span className="font-medium">{user?.email}</span></div>
            <div><span className="text-neutral-500">Rol:</span> <span className="font-medium">{user?.role}</span></div>
          </div>
          <div className="mt-6 pt-4 border-t border-neutral-100 text-xs text-neutral-500 space-y-1.5">
            <div>· Sesión con JWT (12h) + refresh (30d) en cookies httpOnly</div>
            <div>· Contraseñas con bcrypt</div>
            <div>· Bloqueo tras 5 intentos fallidos</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-neutral-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-5 h-5 text-[#FF5A00]" />
            <div className="font-display font-bold text-lg">Cambiar contraseña</div>
          </div>
          <div className="space-y-4">
            <Field label="Contraseña actual">
              <input type="password" data-testid="settings-current-password" required value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Nueva contraseña (mín. 8 caracteres)">
              <input type="password" data-testid="settings-new-password" required value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Confirmar nueva contraseña">
              <input type="password" data-testid="settings-new-password-confirm" required value={next2} onChange={(e) => setNext2(e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="mt-6">
            <PrimaryButton type="submit" data-testid="settings-change-password-submit" disabled={saving}>
              {saving ? "Guardando…" : "Actualizar contraseña"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </PageContainer>
  );
}
